import {
  Box,
  Flex,
  Grid,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal
} from '@chakra-ui/react'
import { UserCircleIcon } from '@heroicons/react/16/solid'
import { Pill } from '@sequencehq/core-components'
import {
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  Lato13Regular
} from '@sequencehq/design-tokens'
import { format } from '@sequencehq/utils/dates'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { INTEGRATION_ICON_URLS, SmallIcon } from 'Integrations/index'
import { ReactNode } from 'react'
import { match } from 'ts-pattern'

const DATE_FORMAT = 'd LLLL yyyy'

const DetailItem = ({
  label,
  content
}: { label: ReactNode; content?: ReactNode }) => {
  return (
    <Grid
      height="32px"
      columnGap="16px"
      templateColumns="158px 1fr"
      alignItems="center"
    >
      <Box {...Lato13Regular} color={GreyGrey70}>
        {label}
      </Box>
      <Box {...Lato13Regular} color={GreyGrey80} position="relative">
        {content}
      </Box>
    </Grid>
  )
}

const Customer = (props: { customerId?: string }) => {
  return (
    <DetailItem
      label="Customer"
      content={
        props.customerId ? (
          <CustomerPreviewCardPill customerId={props.customerId} />
        ) : (
          <Box color={GreyGrey60}>No customer set</Box>
        )
      }
    />
  )
}

export const BillingContactsPopover = (props: {
  contacts: Array<{ id: string; email: string; name: string }>
  trigger: ReactNode
}) => {
  return (
    <Popover trigger="hover" openDelay={250} placement="bottom-start">
      <PopoverTrigger>{props.trigger}</PopoverTrigger>
      <Portal>
        <PopoverContent width="fit-content" minWidth="200px">
          <Box padding="8px 8px 0">
            {props.contacts.map(contact => (
              <Grid
                key={contact.id}
                templateColumns="auto auto 1fr"
                paddingBottom="8px"
                gap="4px"
                {...Lato13Regular}
              >
                <Box paddingRight="4px">
                  <UserCircleIcon
                    color={GreyGrey70}
                    height="16px"
                    width="16px"
                  />
                </Box>
                <Box color={GreyGrey80}>{contact.name}</Box>
                <Box color={GreyGrey60}>{contact.email}</Box>
              </Grid>
            ))}
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

export const BillingContacts = (props: {
  contacts?: Array<{ id: string; email: string; name: string }>
}) => {
  if (!props.contacts) {
    return <DetailItem label="Billing contacts" />
  }

  return (
    <DetailItem
      label="Billing contacts"
      content={
        <BillingContactsPopover
          contacts={props.contacts}
          trigger={
            <Box position="relative">
              <Pill
                interactive
                more={
                  props.contacts.length > 1
                    ? props.contacts.length - 1
                    : undefined
                }
              >
                <Flex gap="8px" alignItems="center">
                  {' '}
                  <UserCircleIcon
                    color={GreyGrey70}
                    height="16px"
                    width="16px"
                  />
                  {props.contacts[0].email}
                </Flex>
              </Pill>
            </Box>
          }
        />
      }
    />
  )
}

export const StartDate = (props: {
  startDate?: Date
}) => {
  return (
    <DetailItem
      label="Start date"
      content={
        props.startDate
          ? format(props.startDate, DATE_FORMAT)
          : 'No start date set'
      }
    />
  )
}

export const EndDate = (props: {
  endDate?: Date
}) => {
  return (
    <DetailItem
      label="End date"
      content={
        props.endDate ? format(props.endDate, DATE_FORMAT) : 'No end date set'
      }
    />
  )
}

export const PaymentMethod = (props: { paymentMethod: 'STRIPE' }) => {
  return (
    <DetailItem
      label="Payment method"
      content={match(props.paymentMethod)
        .with('STRIPE', () => (
          <Flex gap="8px" alignItems="center">
            <SmallIcon
              url={INTEGRATION_ICON_URLS.Stripe?.smallLogoUrl as string}
            />
            Stripe payments
          </Flex>
        ))
        .exhaustive()}
    />
  )
}

export const AutoChargeCustomer = (props: { autoChargeCustomer?: boolean }) => {
  return (
    <DetailItem
      label="Auto-charge customer"
      content={
        <Flex gap="8px" alignItems="center">
          <SmallIcon
            url={INTEGRATION_ICON_URLS.Stripe?.smallLogoUrl as string}
          />
          {props.autoChargeCustomer ? 'Enabled' : 'Disabled'}
        </Flex>
      }
    />
  )
}

export const AutoIssueInvoices = (props: { autoIssueInvoices?: boolean }) => {
  return (
    <DetailItem
      label="Auto-send invoices"
      content={props.autoIssueInvoices ? 'Enabled' : 'Disabled'}
    />
  )
}

export const PurchaseOrderNumber = (props: {
  purchaseOrderNumber?: string
}) => {
  return (
    <DetailItem
      label="PO number"
      content={props.purchaseOrderNumber || 'No PO number set'}
    />
  )
}

export const Label = (props: { label?: string }) => {
  return <DetailItem label="Label" content={props.label || 'No label set'} />
}

export const ScheduleDetailItems = {
  Customer,
  BillingContacts,
  StartDate,
  EndDate,
  PaymentMethod,
  AutoChargeCustomer,
  AutoIssueInvoices,
  PurchaseOrderNumber,
  Label
}
